import {
  DestroyRef,
  ElementRef,
  Injectable,
  NgZone,
  TemplateRef,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  BehaviorSubject,
  Observable,
  animationFrameScheduler,
  combineLatest,
  fromEvent,
  merge,
} from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  map,
  startWith,
  switchMap,
  tap,
  throttleTime,
} from 'rxjs/operators';

export type ConceptualInfotextPayload = {
  key: string;
  text?: string;
  template?: TemplateRef<any>;
};

export type InfoTextData = {
  top: number;
  maxHeight: number;
  text?: string;
  template?: TemplateRef<any>;
} | null;

/** Below this width the infotext is shown as a modal dialog instead of the sidebar. */
export const MOBILE_BREAKPOINT = 1024;

@Injectable({ providedIn: 'root' })
export class InfoTextService {
  private destroyRef = inject(DestroyRef);
  private zone = inject(NgZone);

  private infotextAnchors = new Map<string, ElementRef<HTMLElement>>();
  private infotextContainer$ = new BehaviorSubject<ElementRef<HTMLElement> | null>(null);
  private generalInfotextContainer$ = new BehaviorSubject<ElementRef<HTMLElement> | null>(null);
  private activePayload$ = new BehaviorSubject<ConceptualInfotextPayload | null>(null);
  infotext$ = new BehaviorSubject<InfoTextData | null>(null);
  activeKey$: Observable<string | null> = this.activePayload$.pipe(map(p => p?.key ?? null));

  /** Reactive viewport flag so signal consumers recompute when crossing the breakpoint. */
  readonly isMobile = signal(window.innerWidth < MOBILE_BREAKPOINT);

  constructor() {
    this.setupReactiveUpdates();

    fromEvent(window, 'resize')
      .pipe(
        debounceTime(100),
        map(() => window.innerWidth < MOBILE_BREAKPOINT),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(isMobile => this.isMobile.set(isMobile));
  }

  setContainer(el: ElementRef<HTMLElement>, generalContainer: ElementRef<HTMLElement>): void {
    this.infotextContainer$.next(el);
    this.generalInfotextContainer$.next(generalContainer);
  }

  clearContainer(): void {
    this.infotextContainer$.next(null);
    this.generalInfotextContainer$.next(null);
  }

  register(key: string, el: ElementRef<HTMLElement>): void {
    this.infotextAnchors.set(key, el);
  }

  unregister(key: string): void {
    this.infotextAnchors.delete(key);
  }

  showConceptual(payload: ConceptualInfotextPayload | null): void {
    this.activePayload$.next(payload);
  }

  get hasActiveInfotext(): boolean {
    return this.activePayload$.value !== null;
  }

  private setupReactiveUpdates() {
    // mat-sidenav-content lives inside an @if block, so it may not exist when
    // the service is constructed. Instead of querying once in the constructor
    // (which might return null), we listen on the document with capture:true —
    // scroll events don't bubble but DO propagate in the capture phase, so this
    // catches scroll events from mat-sidenav-content (and any other scroller).
    const viewportChanges$ = this.zone.runOutsideAngular(() =>
      merge(
        new Observable<Event>(subscriber => {
          const handler = (e: Event) => subscriber.next(e);
          document.addEventListener('scroll', handler, { passive: true, capture: true });
          return () =>
            document.removeEventListener('scroll', handler, {
              capture: true,
            });
        }),
        fromEvent(window, 'resize', { passive: true })
      ).pipe(throttleTime(0, animationFrameScheduler, { leading: true, trailing: true }))
    );

    const layoutChanges$ = this.zone.runOutsideAngular(() =>
      new Observable<void>(subscriber => {
        const observer = new MutationObserver(() => subscriber.next());
        observer.observe(document.body, { childList: true, subtree: true });
        return () => observer.disconnect();
      }).pipe(debounceTime(50))
    );

    const positionTriggers$ = merge(viewportChanges$, layoutChanges$).pipe(startWith(0));

    this.activePayload$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        distinctUntilChanged(
          (a, b) => a?.key === b?.key && a?.text === b?.text && a?.template === b?.template
        ),
        switchMap(payload => {
          if (!payload || !payload.key) {
            this.infotext$.next(null);
            return [];
          }

          return combineLatest([
            this.infotextContainer$,
            this.generalContainerWithResize$,
            positionTriggers$,
          ]).pipe(
            map(([container, generalContainer]) =>
              this.calculateInfoTextPosition(container, generalContainer, payload)
            ),
            distinctUntilChanged(
              (a, b) => a?.top === b?.top && a?.text === b?.text && a?.template === b?.template
            ),
            tap(data => {
              this.zone.run(() => this.infotext$.next(data));
            })
          );
        })
      )
      .subscribe();
  }

  private generalContainerWithResize$ = this.generalInfotextContainer$.pipe(
    switchMap(container => {
      if (!container) return [];

      const element = container.nativeElement;

      return new Observable<ElementRef<HTMLElement>>(observer => {
        observer.next(container);

        const resizeObserver = new ResizeObserver(() => {
          observer.next(container);
        });

        resizeObserver.observe(element);
        return () => resizeObserver.disconnect();
      });
    }),
    takeUntilDestroyed(this.destroyRef)
  );

  private calculateInfoTextPosition(
    container: ElementRef<HTMLElement> | null,
    generalContainer: ElementRef<HTMLElement> | null,
    payload: ConceptualInfotextPayload
  ): InfoTextData {
    if (!container || !generalContainer) return null;

    const anchor = this.infotextAnchors.get(payload.key)?.nativeElement;
    const cont = container.nativeElement;
    const generalCont = generalContainer.nativeElement;

    if (!anchor || !cont) return null;

    const anchorTop = window.scrollY + anchor.getBoundingClientRect().top;
    const contTop = window.scrollY + cont.getBoundingClientRect().top;
    const rawTop = Math.round(anchorTop - contTop);
    const hasGeneralCard = generalCont.childElementCount > 0;
    const generalHeight = generalCont.getBoundingClientRect().height;
    const floor = hasGeneralCard ? Math.round(generalHeight + 30) : -Infinity;
    const top = Math.max(rawTop, floor);

    const bottomMargin = 100;
    const minCardHeight = 220;

    const maxHeight = Math.max(minCardHeight, window.innerHeight - top - bottomMargin);

    return {
      top,
      maxHeight,
      text: payload.text,
      template: payload.template,
    };
  }
}
